import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, Filter, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, PAYMENT_METHOD_LABEL } from "@/lib/workflow";

const ALL = "all";

export default function Vouchers({ cc = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [filters, setFilters] = useState({
    q: "", vendor_id: ALL, status: ALL, method: ALL,
    date_from: "", date_to: "", amount_min: "", amount_max: "",
  });

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, val]) => {
      if (val && val !== ALL) params[k] = val;
    });
    try {
      const { data } = await api.get("/vouchers", { params: cc ? { ...params, cc: true } : params });
      setVouchers(data);
    } catch (e) {
      toast.error(apiError(e));
      setVouchers([]);
    }
  }, [filters, cc]);

  useEffect(() => {
    api.get("/vendors").then(({ data }) => setVendors(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () =>
    setFilters({ q: "", vendor_id: ALL, status: ALL, method: ALL, date_from: "", date_to: "", amount_min: "", amount_max: "" });

  return (
    <div className="space-y-6" data-testid={cc ? "cc-vouchers-page" : "vouchers-page"}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">{cc ? "CC Vouchers" : "Vouchers"}</h2>
          <p className="text-sm text-slate-500">{vouchers ? `${vouchers.length} voucher(s)${cc ? " shared with you (view only)" : ""}` : "Loading…"}</p>
        </div>
        {!cc && user?.role === "creator" && (
          <Button onClick={() => navigate("/vouchers/new")} className="gap-2" data-testid="create-voucher-btn">
            <Plus className="w-4 h-4" /> New Voucher
          </Button>
        )}
      </div>

      <Card className="p-4 md:p-5 border-slate-200 dark:border-slate-800 space-y-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-500">
          <Filter className="w-3.5 h-3.5" /> Search &amp; Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Search voucher no, vendor, reference…"
              value={filters.q}
              onChange={(e) => set("q", e.target.value)}
              data-testid="filter-search"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {Object.entries(STATUS_META).filter(([k]) => !cc || k !== "draft").map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.vendor_id} onValueChange={(v) => set("vendor_id", v)}>
            <SelectTrigger data-testid="filter-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All vendors</SelectItem>
              {vendors.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select value={filters.method} onValueChange={(v) => set("method", v)}>
            <SelectTrigger data-testid="filter-method"><SelectValue placeholder="Method" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All methods</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" />
          <Input type="number" placeholder="Min AED" value={filters.amount_min} onChange={(e) => set("amount_min", e.target.value)} data-testid="filter-amount-min" />
          <Input type="number" placeholder="Max AED" value={filters.amount_max} onChange={(e) => set("amount_max", e.target.value)} data-testid="filter-amount-max" />
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={reset} className="gap-1.5 text-slate-500" data-testid="filter-reset">
            <X className="w-3.5 h-3.5" /> Clear filters
          </Button>
        </div>
      </Card>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
        {vouchers === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : vouchers.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-slate-500" data-testid="vouchers-empty">{cc ? "No vouchers have been shared with you yet." : "No vouchers match your filters."}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Voucher Type</TableHead>
                <TableHead>Voucher No</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vouchers.map((v) => (
                <TableRow
                  key={v.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/vouchers/${v.id}`, { state: { from: cc ? "/cc" : "/vouchers" } })}
                  data-testid={`voucher-row-${v.voucher_no}`}
                >
                  <TableCell className="font-medium text-sm whitespace-nowrap">{v.company_name || "—"}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap text-slate-600 dark:text-slate-300">{v.voucher_type || "—"}</TableCell>
                  <TableCell className="font-mono text-sm font-medium">{v.voucher_no}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{v.vendor_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(v.date)}</TableCell>
                  <TableCell className="text-sm">{PAYMENT_METHOD_LABEL[v.payment_method] || v.payment_method}</TableCell>
                  <TableCell className="text-right font-mono font-medium">{formatMoney(v.grand_total, v.currency)}</TableCell>
                  <TableCell><StatusBadge status={v.status} locked={v.locked} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
